// Country-specific business registration profiles
export interface CountryProfile {
  code: string;
  registrationNumberLabel: string;
  registrationNumberPlaceholder: string;
  registrationNumberHint?: string;
  taxIdLabel: string;
  taxIdPlaceholder: string;
  taxIdHint?: string;
  localFields?: LocalFieldConfig[];
}

export interface LocalFieldConfig {
  key: string;
  label: string;
  placeholder: string;
  hint?: string;
  required?: boolean;
}

// Country-specific business registration configurations
export const countryProfiles: Record<string, CountryProfile> = {
  // Israel
  IL: {
    code: 'IL',
    registrationNumberLabel: 'Company Number (ח.פ)',
    registrationNumberPlaceholder: '514123456',
    registrationNumberHint: 'Israeli company registration number',
    taxIdLabel: 'VAT Number (עוסק מורשה)',
    taxIdPlaceholder: '123456789',
    taxIdHint: 'Israeli VAT registration number',
    localFields: [
      {
        key: 'deductions_portfolio',
        label: 'Deductions Portfolio (תיק ניכויים)',
        placeholder: '1234567',
        hint: 'Israeli tax deductions portfolio number',
        required: false,
      },
    ],
  },

  // Canada
  CA: {
    code: 'CA',
    registrationNumberLabel: 'Incorporation Number',
    registrationNumberPlaceholder: '123456789',
    registrationNumberHint: 'Federal or provincial incorporation number',
    taxIdLabel: 'Business Number (BN)',
    taxIdPlaceholder: '123456789RC0001',
    taxIdHint: '9-digit BN with program account identifier',
    localFields: [
      {
        key: 'cra_program_account',
        label: 'CRA Program Account',
        placeholder: 'RT0001',
        hint: 'e.g., RT0001 for GST/HST, RP0001 for Payroll',
        required: false,
      },
    ],
  },

  // United States
  US: {
    code: 'US',
    registrationNumberLabel: 'State Registration Number',
    registrationNumberPlaceholder: '202312345678',
    registrationNumberHint: 'State-issued business registration number',
    taxIdLabel: 'EIN (Employer Identification Number)',
    taxIdPlaceholder: '12-3456789',
    taxIdHint: '9-digit IRS employer identification number',
  },

  // United Kingdom
  GB: {
    code: 'GB',
    registrationNumberLabel: 'Company Registration Number',
    registrationNumberPlaceholder: '12345678',
    registrationNumberHint: 'Companies House registration number',
    taxIdLabel: 'VAT Registration Number',
    taxIdPlaceholder: 'GB123456789',
    taxIdHint: 'UK VAT number (if registered)',
  },

  // Default profile for all other countries
  DEFAULT: {
    code: 'DEFAULT',
    registrationNumberLabel: 'Registration Number',
    registrationNumberPlaceholder: '123456789',
    registrationNumberHint: 'Official business registration number',
    taxIdLabel: 'Tax ID / VAT Number',
    taxIdPlaceholder: '12-3456789',
    taxIdHint: 'Tax identification or VAT number',
  },
};

// Helper function to get country profile
export function getCountryProfile(countryCode: string | null): CountryProfile {
  if (!countryCode) {
    return countryProfiles.DEFAULT as CountryProfile;
  }
  const profile = countryProfiles[countryCode];
  if (profile) {
    return profile as CountryProfile;
  }
  return countryProfiles.DEFAULT as CountryProfile;
}
